import React, { useState } from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import {
  Buildings,
  CheckCircle,
  PaperPlaneRight,
  Info,
  Calendar,
  ArrowRight,
} from "@phosphor-icons/react";
import { API_BASE } from "../lib/site";
import SectionHeading from "../components/SectionHeading";
import Seo from "../components/Seo";

const ORG_TYPES = [
  ["board_and_care", "Board & Care"],
  ["assisted_living", "Assisted Living / Senior Living"],
  ["hospice", "Hospice"],
  ["hospital", "Hospital / Healthcare Organization"],
  ["church", "Church / Faith Community"],
  ["business", "Business / Employer"],
  ["community", "Community Organization"],
  ["other", "Other"],
];

const PROGRAMS = [
  ["from_diagnosis_to_goodbye_presentation", "From Diagnosis to Goodbye Presentation"],
  ["hospice_family_education", "Hospice Family Education"],
  ["grief_bereavement_support", "Grief & Bereavement Support"],
  ["caregiver_support", "Caregiver Support"],
  ["staff_grief_support", "Staff Grief Support"],
  ["compassion_fatigue_burnout", "Compassion Fatigue / Burnout Support"],
  ["devotional_music", "Devotional & Music Program"],
  ["workshop_training", "Workshop / Training"],
  ["speaking_engagement", "Speaking Engagement"],
  ["other", "Other"],
];

const AUDIENCE_HINTS = [
  "Residents",
  "Patients / Families",
  "Caregivers",
  "Staff / Employees",
  "Leadership",
  "Congregation",
  "Community Members",
  "Mixed Audience",
];

const initialForm = {
  organization_name: "",
  organization_type: "",
  organization_type_other: "",
  contact_first_name: "",
  contact_last_name: "",
  title: "",
  email: "",
  phone: "",
  preferred_contact: "email",
  program: "",
  program_other: "",
  estimated_attendance: "",
  audience_type: "",
  format: "in_person",
  city: "",
  state: "",
  venue_address: "",
  preferred_date: "",
  preferred_start_time: "",
  alternate_date: "",
  alternate_start_time: "",
  additional_info: "",
  consent: false,
};

const OrganizationBookings = () => {
  const [form, setForm] = useState(initialForm);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState(null); // { program_label, preferred_date }

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (
      !form.organization_name.trim() ||
      !form.organization_type ||
      !form.contact_first_name.trim() ||
      !form.contact_last_name.trim() ||
      !form.email.trim() ||
      !form.program ||
      !form.preferred_date ||
      !form.preferred_start_time
    ) {
      setError("Please complete all required fields.");
      return;
    }
    if (form.organization_type === "other" && !form.organization_type_other.trim()) {
      setError("Please briefly describe your organization type.");
      return;
    }
    if (form.program === "other" && !form.program_other.trim()) {
      setError("Please briefly describe the program you're requesting.");
      return;
    }
    if (form.format === "in_person" && (!form.city.trim() || !form.state.trim())) {
      setError("Please provide the city and state for in-person events.");
      return;
    }
    if (!form.consent) {
      setError("Please agree to be contacted to submit your request.");
      return;
    }
    setSubmitting(true);
    try {
      const payload = {
        organization_name: form.organization_name.trim(),
        organization_type: form.organization_type,
        organization_type_other: form.organization_type_other.trim() || null,
        contact_first_name: form.contact_first_name.trim(),
        contact_last_name: form.contact_last_name.trim(),
        title: form.title.trim() || null,
        email: form.email.trim(),
        phone: form.phone.trim() || null,
        preferred_contact: form.preferred_contact,
        program: form.program,
        program_other: form.program_other.trim() || null,
        estimated_attendance: form.estimated_attendance
          ? Number(form.estimated_attendance)
          : null,
        audience_type: form.audience_type.trim() || null,
        format: form.format,
        city: form.city.trim() || null,
        state: form.state.trim() || null,
        venue_address: form.venue_address.trim() || null,
        preferred_date: form.preferred_date,
        preferred_start_time:
          form.preferred_start_time.length === 5
            ? `${form.preferred_start_time}:00`
            : form.preferred_start_time,
        alternate_date: form.alternate_date || null,
        alternate_start_time: form.alternate_start_time
          ? form.alternate_start_time.length === 5
            ? `${form.alternate_start_time}:00`
            : form.alternate_start_time
          : null,
        additional_info: form.additional_info.trim() || null,
        consent: true,
        source_page:
          typeof window !== "undefined" ? window.location.pathname : null,
      };
      const res = await axios.post(`${API_BASE}/organization-bookings`, payload);
      setSuccess({
        program_label: res.data.program_label,
        preferred_date: res.data.preferred_date,
      });
      toast.success("Booking request received.");
      // Scroll to top of section
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch (err) {
      const detail = err?.response?.data?.detail;
      let msg = "We couldn't submit your request. Please try again.";
      if (typeof detail === "string") msg = detail;
      else if (Array.isArray(detail) && detail[0]?.msg) msg = detail[0].msg;
      setError(msg);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div data-testid="page-organization-bookings">
      <Seo
        title="Book a Presentation or Program for Your Organization"
        description="Request presentations, workshops, support programs, trainings, and speaking engagements for your board & care, senior living, hospice, hospital, church, or business."
      />

      {/* Hero */}
      <section className="pt-32 pb-14 sm:pt-40 sm:pb-16 bg-gradient-to-br from-softblue/50 via-white to-secondary/10">
        <div className="container-x max-w-4xl text-center">
          <span className="inline-flex items-center gap-2 rounded-full bg-white border border-primary/20 px-3 py-1 text-[10px] uppercase tracking-[0.22em] font-semibold text-primary">
            <Buildings size={14} weight="duotone" />
            For Organizations
          </span>
          <h1 className="mt-6 font-heading text-4xl sm:text-5xl lg:text-6xl font-medium text-ink-900 leading-[1.05]">
            Book a Presentation or Program
          </h1>
          <p className="mt-6 text-ink-700 text-lg leading-relaxed max-w-3xl mx-auto">
            Bring compassionate education, support, and meaningful conversation
            to your organization. Begin Again Living Ministries provides
            presentations, workshops, support programs, and training for
            organizations serving patients, families, caregivers, employees,
            and communities.
          </p>
          <a
            href="#request-form"
            data-testid="ob-hero-cta"
            className="mt-8 inline-flex items-center gap-2 rounded-full bg-primary text-white px-7 py-3.5 font-semibold hover:bg-primary-dark hover:-translate-y-0.5 hover:shadow-lg transition-all"
          >
            Request a Date &amp; Program
            <ArrowRight size={16} weight="bold" />
          </a>
          <p className="mt-3 text-xs text-ink-500">
            Requests are reviewed and confirmed by Begin Again Living Ministries.
          </p>
        </div>
      </section>

      {success ? (
        <section className="py-20 sm:py-24">
          <div
            className="container-x max-w-2xl text-center"
            data-testid="ob-success"
          >
            <span className="mx-auto inline-flex h-16 w-16 items-center justify-center rounded-full bg-primary/10 text-primary">
              <CheckCircle size={36} weight="duotone" />
            </span>
            <h2 className="mt-5 font-heading text-3xl sm:text-4xl font-medium text-ink-900">
              Booking Request Received
            </h2>
            <p className="mt-4 text-ink-700 leading-relaxed">
              Thank you for contacting Begin Again Living Ministries. We have
              received your request for <strong>{success.program_label}</strong>{" "}
              on <strong>{success.preferred_date}</strong>.
            </p>
            <p className="mt-3 text-ink-700 leading-relaxed">
              Your requested date and time are not yet confirmed. We will review
              your request and contact you regarding availability, program
              details, and next steps.
            </p>
            <p className="mt-3 text-ink-700 leading-relaxed">
              We look forward to learning more about how we can support your
              organization.
            </p>
            <div className="mt-8 flex flex-wrap gap-3 justify-center">
              <Link
                to="/"
                className="rounded-full bg-primary text-white px-6 py-2.5 font-semibold text-sm hover:bg-primary-dark transition-colors"
              >
                Return home
              </Link>
              <Link
                to="/contact"
                className="rounded-full bg-white border border-ink-200 text-ink-800 px-6 py-2.5 font-semibold text-sm hover:border-primary hover:text-primary transition-colors"
              >
                Contact the ministry
              </Link>
            </div>
          </div>
        </section>
      ) : (
        <section id="request-form" className="py-16 sm:py-20 bg-white">
          <div className="container-x max-w-3xl">
            <SectionHeading
              overline="Booking Request"
              title="Tell us about your organization and event"
              className="mb-10"
            />
            <div className="rounded-2xl border border-primary/15 bg-softblue/30 p-4 mb-8 flex gap-3">
              <Info size={18} weight="duotone" className="text-primary shrink-0 mt-0.5" />
              <p className="text-sm text-ink-700 leading-relaxed">
                Requested dates and times are not confirmed until you receive
                confirmation from Begin Again Living Ministries.
              </p>
            </div>

            <form onSubmit={submit} className="space-y-10" data-testid="ob-form">
              {/* Organization */}
              <Section title="Organization Information">
                <Field label="Organization name" required>
                  <input
                    type="text"
                    required
                    value={form.organization_name}
                    onChange={(e) => set("organization_name", e.target.value)}
                    data-testid="ob-org-name"
                    className={inputCls}
                  />
                </Field>
                <Field label="Organization type" required>
                  <select
                    required
                    value={form.organization_type}
                    onChange={(e) => set("organization_type", e.target.value)}
                    data-testid="ob-org-type"
                    className={inputCls}
                  >
                    <option value="">Select an organization type…</option>
                    {ORG_TYPES.map(([v, l]) => (
                      <option key={v} value={v}>
                        {l}
                      </option>
                    ))}
                  </select>
                </Field>
                {form.organization_type === "other" && (
                  <Field label="Please describe">
                    <input
                      type="text"
                      value={form.organization_type_other}
                      onChange={(e) => set("organization_type_other", e.target.value)}
                      data-testid="ob-org-type-other"
                      className={inputCls}
                    />
                  </Field>
                )}
              </Section>

              {/* Contact */}
              <Section title="Contact Information">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <Field label="First name" required>
                    <input
                      type="text"
                      required
                      value={form.contact_first_name}
                      onChange={(e) => set("contact_first_name", e.target.value)}
                      data-testid="ob-contact-first"
                      className={inputCls}
                    />
                  </Field>
                  <Field label="Last name" required>
                    <input
                      type="text"
                      required
                      value={form.contact_last_name}
                      onChange={(e) => set("contact_last_name", e.target.value)}
                      data-testid="ob-contact-last"
                      className={inputCls}
                    />
                  </Field>
                </div>
                <Field label="Title / Role" hint="Optional">
                  <input
                    type="text"
                    value={form.title}
                    onChange={(e) => set("title", e.target.value)}
                    data-testid="ob-title"
                    className={inputCls}
                  />
                </Field>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <Field label="Email address" required>
                    <input
                      type="email"
                      required
                      value={form.email}
                      onChange={(e) => set("email", e.target.value)}
                      data-testid="ob-email"
                      className={inputCls}
                    />
                  </Field>
                  <Field label="Phone number" hint="Optional">
                    <input
                      type="tel"
                      value={form.phone}
                      onChange={(e) => set("phone", e.target.value)}
                      data-testid="ob-phone"
                      className={inputCls}
                    />
                  </Field>
                </div>
                <div>
                  <span className="text-xs font-semibold text-ink-700">
                    Preferred contact method
                  </span>
                  <div className="mt-1 flex gap-4">
                    {[
                      ["email", "Email"],
                      ["phone", "Phone"],
                      ["text", "Text"],
                    ].map(([v, l]) => (
                      <label key={v} className="inline-flex items-center gap-2 text-sm text-ink-800">
                        <input
                          type="radio"
                          name="preferred_contact"
                          value={v}
                          checked={form.preferred_contact === v}
                          onChange={() => set("preferred_contact", v)}
                          data-testid={`ob-contact-method-${v}`}
                          className="text-primary focus:ring-primary"
                        />
                        {l}
                      </label>
                    ))}
                  </div>
                </div>
              </Section>

              {/* Program */}
              <Section title="Program Requested">
                <Field label="Program" required>
                  <select
                    required
                    value={form.program}
                    onChange={(e) => set("program", e.target.value)}
                    data-testid="ob-program"
                    className={inputCls}
                  >
                    <option value="">Select a program…</option>
                    {PROGRAMS.map(([v, l]) => (
                      <option key={v} value={v}>
                        {l}
                      </option>
                    ))}
                  </select>
                </Field>
                {form.program === "other" && (
                  <Field label="Please describe">
                    <input
                      type="text"
                      value={form.program_other}
                      onChange={(e) => set("program_other", e.target.value)}
                      data-testid="ob-program-other"
                      className={inputCls}
                    />
                  </Field>
                )}
              </Section>

              {/* Audience */}
              <Section title="Audience & Event">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <Field label="Estimated participants" hint="Optional">
                    <input
                      type="number"
                      min="0"
                      value={form.estimated_attendance}
                      onChange={(e) => set("estimated_attendance", e.target.value)}
                      data-testid="ob-attendance"
                      className={inputCls}
                    />
                  </Field>
                  <Field label="Audience type" hint="Optional">
                    <input
                      type="text"
                      list="ob-audience-hints"
                      value={form.audience_type}
                      onChange={(e) => set("audience_type", e.target.value)}
                      placeholder="e.g. Residents, Staff, Mixed Audience"
                      data-testid="ob-audience"
                      className={inputCls}
                    />
                    <datalist id="ob-audience-hints">
                      {AUDIENCE_HINTS.map((h) => (
                        <option key={h} value={h} />
                      ))}
                    </datalist>
                  </Field>
                </div>
                <div>
                  <span className="text-xs font-semibold text-ink-700">Format</span>
                  <div className="mt-1 flex gap-4 flex-wrap">
                    {[
                      ["in_person", "In Person"],
                      ["online", "Online"],
                      ["either", "Either / Open to discussion"],
                    ].map(([v, l]) => (
                      <label key={v} className="inline-flex items-center gap-2 text-sm text-ink-800">
                        <input
                          type="radio"
                          name="format"
                          value={v}
                          checked={form.format === v}
                          onChange={() => set("format", v)}
                          data-testid={`ob-format-${v}`}
                          className="text-primary focus:ring-primary"
                        />
                        {l}
                      </label>
                    ))}
                  </div>
                </div>
                {form.format === "in_person" && (
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <Field label="City" required>
                      <input
                        type="text"
                        required
                        value={form.city}
                        onChange={(e) => set("city", e.target.value)}
                        data-testid="ob-city"
                        className={inputCls}
                      />
                    </Field>
                    <Field label="State" required>
                      <input
                        type="text"
                        required
                        value={form.state}
                        onChange={(e) => set("state", e.target.value)}
                        data-testid="ob-state"
                        className={inputCls}
                      />
                    </Field>
                    <div className="sm:col-span-2">
                      <Field label="Venue / Organization address" hint="Optional at this stage">
                        <input
                          type="text"
                          value={form.venue_address}
                          onChange={(e) => set("venue_address", e.target.value)}
                          data-testid="ob-venue"
                          className={inputCls}
                        />
                      </Field>
                    </div>
                  </div>
                )}
              </Section>

              {/* Date / Time */}
              <Section title="Date & Time Request">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <Field label="Preferred date" required>
                    <input
                      type="date"
                      required
                      value={form.preferred_date}
                      onChange={(e) => set("preferred_date", e.target.value)}
                      data-testid="ob-preferred-date"
                      className={inputCls}
                    />
                  </Field>
                  <Field label="Preferred start time" required>
                    <input
                      type="time"
                      required
                      value={form.preferred_start_time}
                      onChange={(e) => set("preferred_start_time", e.target.value)}
                      data-testid="ob-preferred-time"
                      className={inputCls}
                    />
                  </Field>
                  <Field label="Alternate date" hint="Optional">
                    <input
                      type="date"
                      value={form.alternate_date}
                      onChange={(e) => set("alternate_date", e.target.value)}
                      data-testid="ob-alternate-date"
                      className={inputCls}
                    />
                  </Field>
                  <Field label="Alternate start time" hint="Optional">
                    <input
                      type="time"
                      value={form.alternate_start_time}
                      onChange={(e) => set("alternate_start_time", e.target.value)}
                      data-testid="ob-alternate-time"
                      className={inputCls}
                    />
                  </Field>
                </div>
                <p className="text-xs text-ink-500 flex items-start gap-2">
                  <Calendar size={12} weight="bold" className="text-primary mt-0.5 shrink-0" />
                  Requested dates and times are not confirmed until you receive confirmation from Begin Again Living Ministries.
                </p>
              </Section>

              {/* Additional info */}
              <Section title="Additional Information">
                <Field label="Anything else that would help us understand your needs?" hint="Optional">
                  <textarea
                    rows={5}
                    maxLength={4000}
                    value={form.additional_info}
                    onChange={(e) => set("additional_info", e.target.value)}
                    data-testid="ob-additional"
                    className={inputCls + " resize-none"}
                  />
                </Field>
                <p className="text-xs text-ink-500">
                  Please do not include confidential medical, patient, employee,
                  or other sensitive personal information.
                </p>
              </Section>

              {/* Consent */}
              <label className="flex items-start gap-2.5 rounded-xl border border-ink-100 bg-softblue/20 p-3">
                <input
                  type="checkbox"
                  required
                  checked={form.consent}
                  onChange={(e) => set("consent", e.target.checked)}
                  data-testid="ob-consent"
                  className="mt-0.5 text-primary focus:ring-primary"
                />
                <span className="text-xs leading-snug text-ink-700">
                  I agree to be contacted by Begin Again Living Ministries
                  regarding this booking request.
                </span>
              </label>

              {error && (
                <div
                  role="alert"
                  data-testid="ob-error"
                  className="rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700"
                >
                  {error}
                </div>
              )}

              <div>
                <button
                  type="submit"
                  disabled={submitting}
                  data-testid="ob-submit"
                  className="inline-flex items-center gap-2 rounded-full bg-primary text-white px-7 py-3.5 font-semibold hover:bg-primary-dark hover:-translate-y-0.5 hover:shadow-lg transition-all disabled:opacity-60"
                >
                  <PaperPlaneRight size={16} weight="bold" />
                  {submitting ? "Submitting..." : "Submit Booking Request"}
                </button>
              </div>
            </form>
          </div>
        </section>
      )}
    </div>
  );
};

const inputCls =
  "mt-1 block w-full rounded-lg border border-ink-200 px-3 py-2 text-sm focus:border-primary focus:ring-2 focus:ring-primary/20 outline-none";

const Section = ({ title, children }) => (
  <div className="space-y-4">
    <h3 className="font-heading text-xl text-ink-900 font-medium border-b border-ink-100 pb-2">
      {title}
    </h3>
    {children}
  </div>
);

const Field = ({ label, hint, required, children }) => (
  <label className="block">
    <span className="text-xs font-semibold text-ink-700">
      {label} {required && <span className="text-red-500">*</span>}
      {hint && !required && <span className="text-ink-400 font-normal ml-1">({hint})</span>}
    </span>
    {children}
  </label>
);

export default OrganizationBookings;
